/**
 * CompanyRepository - Accès aux entreprises dans Supabase
 *
 * Créé en même temps que le lien public client : jusqu'ici les entreprises
 * étaient interrogées par des `supabase.from('companies')` disséminés dans six
 * fichiers, ce que `CLAUDE.md` interdit. Le neuf passe par ici ; la reprise des
 * six appels existants est planifiée (lot 3 de la spec lien public).
 *
 * ATTENTION : `public_token` est un secret. Aucune méthode ne doit le renvoyer
 * dans une requête accessible aux prestataires — seules les méthodes
 * `*PublicPage`, réservées aux admins par RLS, y touchent.
 */

import { BaseRepository } from './BaseRepository';
import { generatePublicPageToken } from '@/utils/publicPage';
import type { CompanyPublicPage } from '@/types/public-page.types';
import type { ClientWithProjects } from '@/types/client-admin.types';

/** Colonnes sûres : jamais de wildcard sur `companies` depuis l'ajout du token */
const SAFE_COLUMNS = 'id, name, logo_url, primary_color, secondary_color';
const PUBLIC_PAGE_COLUMNS = 'id, name, public_slug, public_token, public_page_enabled';
/** Fiche entreprise de l'admin (coordonnées) avec ses projets, sans le token */
const ADMIN_COLUMNS = `
  id,
  name,
  logo_url,
  contact_email,
  contact_phone,
  address,
  created_at,
  updated_at,
  projects (
    id,
    name
  )
`;

/** Champs éditables d'une entreprise depuis la page admin Entreprises */
export interface CompanyContactFields {
  name: string;
  logo_url: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
}

interface CompanyOptionRow {
  id: string;
  name: string;
}

interface PublicPageRow {
  id: string;
  name: string;
  public_slug: string | null;
  public_token: string | null;
  public_page_enabled: boolean;
}

export interface SavePublicPageInput {
  slug: string;
  enabled: boolean;
}

function toPublicPage(row: PublicPageRow): CompanyPublicPage {
  return {
    companyId: row.id,
    companyName: row.name,
    slug: row.public_slug,
    token: row.public_token,
    enabled: row.public_page_enabled,
  };
}

export class CompanyRepository extends BaseRepository {
  private static readonly TABLE = 'companies';

  /** Liste minimale (id + nom) pour les sélecteurs */
  static async listOptions(): Promise<CompanyOptionRow[]> {
    return this.executeQueryArray<CompanyOptionRow>(
      this.supabase.from(this.TABLE).select('id, name').order('name', { ascending: true }),
      'Fetch company options'
    );
  }

  /**
   * Entreprises avec coordonnées et projets (page admin Entreprises),
   * triées par nom, projets triés par nom.
   *
   * Les écritures admin ci-dessous sont appelées sans contexte d'erreur : leur
   * message est affiché tel quel à l'utilisateur et doit rester celui de Supabase.
   */
  static async listWithProjects(): Promise<ClientWithProjects[]> {
    return this.executeQueryArray<ClientWithProjects>(
      this.supabase
        .from(this.TABLE)
        .select(ADMIN_COLUMNS)
        .order('name', { ascending: true })
        .order('name', { ascending: true, foreignTable: 'projects' })
    );
  }

  /** Crée une entreprise sans relire la ligne */
  static async insert(fields: CompanyContactFields): Promise<void> {
    const { error } = await this.supabase
      .from(this.TABLE)
      .insert([fields]);

    if (error) throw this.handleError(error);
  }

  /** Met à jour les coordonnées d'une entreprise sans relire la ligne */
  static async updateFields(id: string, fields: CompanyContactFields): Promise<void> {
    const { error } = await this.supabase
      .from(this.TABLE)
      .update(fields)
      .eq('id', id);

    if (error) throw this.handleError(error);
  }

  /** Supprime une entreprise */
  static async delete(id: string): Promise<void> {
    const { error } = await this.supabase
      .from(this.TABLE)
      .delete()
      .eq('id', id);

    if (error) throw this.handleError(error);
  }

  /** Nombre total d'entreprises (dashboard admin) */
  static async count(): Promise<number> {
    return this.executeCount(
      this.supabase.from(this.TABLE).select('id', { count: 'exact', head: true }),
      'Count companies'
    );
  }

  /** Nom d'une entreprise, pour les libellés (journal d'audit) */
  static async getNameById(id: string): Promise<string | null> {
    const row = await this.executeQuery<{ name: string }>(
      this.supabase.from(this.TABLE).select('name').eq('id', id).single(),
      `Fetch company name ${id}`
    );
    return row?.name ?? null;
  }

  /** Colonnes non sensibles d'une entreprise */
  static async getById(id: string) {
    return this.executeQuery(
      this.supabase.from(this.TABLE).select(SAFE_COLUMNS).eq('id', id).single(),
      `Fetch company ${id}`
    );
  }

  /**
   * Entreprise désignée par le lien public de la requête courante.
   *
   * Aucun filtre : les policies `*_public_link_view` font que `companies` ne
   * contient qu'une seule ligne visible pour un visiteur par lien. Renvoie null
   * si le lien est absent, faux ou désactivé.
   */
  static async getByPublicLink(): Promise<{ id: string; name: string; logo_url: string | null } | null> {
    const rows = await this.executeQueryArray<{ id: string; name: string; logo_url: string | null }>(
      this.supabase.from(this.TABLE).select('id, name, logo_url').limit(1),
      'Fetch company by public link'
    );
    return rows[0] ?? null;
  }

  // ----- Lien public (admins uniquement, via RLS) -----

  /** Réglages du lien public. Renvoie toujours un objet, même si rien n'est configuré. */
  static async getPublicPage(companyId: string): Promise<CompanyPublicPage> {
    const row = await this.executeQuery<PublicPageRow>(
      this.supabase.from(this.TABLE).select(PUBLIC_PAGE_COLUMNS).eq('id', companyId).single(),
      `Fetch public page ${companyId}`
    );
    return toPublicPage(row);
  }

  /**
   * Enregistre slug et activation.
   *
   * Un token est généré à la première sauvegarde : la contrainte
   * `companies_public_page_complete` refuse un lien activé sans token.
   */
  static async savePublicPage(
    companyId: string,
    input: SavePublicPageInput,
    currentToken: string | null
  ): Promise<CompanyPublicPage> {
    const row = await this.executeMutation<PublicPageRow>(
      this.supabase
        .from(this.TABLE)
        .update({
          public_slug: input.slug,
          public_token: currentToken ?? generatePublicPageToken(),
          public_page_enabled: input.enabled,
        })
        .eq('id', companyId)
        .select(PUBLIC_PAGE_COLUMNS)
        .single(),
      `Save public page ${companyId}`
    );
    return toPublicPage(row);
  }

  /** Nouveau token, slug conservé : l'ancien lien cesse immédiatement de fonctionner */
  static async regeneratePublicToken(companyId: string): Promise<string> {
    const row = await this.executeMutation<{ public_token: string }>(
      this.supabase
        .from(this.TABLE)
        .update({ public_token: generatePublicPageToken() })
        .eq('id', companyId)
        .select('public_token')
        .single(),
      `Regenerate public token ${companyId}`
    );
    return row.public_token;
  }
}
